import type { LoginPageOption, LoginPageService, Plugin } from '@/plugins'
import { PLUGIN_AUTH } from '@/plugins/builtin/BuiltinPluginSymbol'
import { RESETPASSWORD_PATH, SIGNIN_PATH, SIGNUP_PATH } from '@/plugins/builtin/login_page/constants'
import { injectOptional } from '@/tsyringe-extend'
import Cookie from 'js-cookie'
import { container, injectable, instanceCachingFactory, registry, singleton } from 'tsyringe'
import type { App } from 'vue'
import { createRouter, createWebHistory, type RouteLocationNormalized, type Router } from 'vue-router'
import { CORE_INITIALIZER, CORE_ROUTER, CORE_ROUTER_EXTEND, CORE_USER } from '../CoreSymbol'
import type { UserService } from './'
import type { Initializer } from './Initializer'

/**
 * @description 路由拓展服务
 * @param {boolean} enable 服务开关
 */
export type RouteExtendOptions = {
  /** enable 服务开关 */
  enable: boolean
}

/**
 * @param RouteExtendInjectKey 路由模块服务注册Token
 */
export const RouteExtendInjectKey = "RouteExtendInjectKey"
/**
 *  Vue-router 的基础上拓展,提供给外部的接口服务
 */
export class RouteExtend {
  options: Partial<RouteExtendOptions>
  constructor() {

    this.options = {}
  }

  /**
  * @description 用户路由鉴权服务
  * @param routerArray - (路由表结构)
  * @see {@link RouteState 路由结构}
  */
  setOptions(options: RouteExtendOptions): void {
    this.options = options
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authRouter(to: RouteLocationNormalized, from: RouteLocationNormalized): any {
    const userService = container.resolve<UserService>(CORE_USER)
    const loginOptions = container.resolve<Plugin<LoginPageOption, LoginPageService>>(PLUGIN_AUTH)
    const isUserValid = userService.isUserValid()
    const cookie = Cookie.get(loginOptions.options.cookieKey || 'iota')
    const isLogged = isUserValid && cookie && cookie.length > 0
    const whiteMap = new Map([
      [SIGNIN_PATH, SIGNIN_PATH],
      [SIGNUP_PATH, SIGNUP_PATH],
      [RESETPASSWORD_PATH, RESETPASSWORD_PATH]
    ])
    const clickRouter = to.fullPath
    const winState = window.history.state
    const query = to.query
    if (loginOptions.options.recordDisabled === true) {
      loginOptions.options.recordPath = from.fullPath
    }
    if (to.redirectedFrom?.query.fallback) {
      loginOptions.options.recordPath = to.redirectedFrom?.query.fallback ? to.redirectedFrom?.query.fallback as string : "/"
    } else if (loginOptions.options.recordDisabled === false) {
      loginOptions.options.recordPath = loginOptions.options.redirectTo
    }
    if (to.fullPath != SIGNUP_PATH && clickRouter != RESETPASSWORD_PATH && clickRouter != RESETPASSWORD_PATH) {
      clickRouter == '/signin' && !isLogged ? '' : sessionStorage.setItem('toPath', clickRouter)
      clickRouter == '/signin' && !isLogged ? '' : sessionStorage.setItem('toQuery', JSON.stringify(query))
    }

    if (to.fullPath == '/') {
      return { path: loginOptions.options.redirectTo || '/' }
    } else if (!whiteMap.get(to.fullPath) && !isLogged) {
      return { path: SIGNIN_PATH }
    } else if (whiteMap.get(to.fullPath) && !isLogged) {
      return
    } else if (clickRouter && isLogged) {
      sessionStorage.removeItem('toPath')
      sessionStorage.removeItem('toQuery')
    }

    if (winState.forward && winState.current == SIGNIN_PATH && isLogged) {
      history.go(-1)
    }

  }
}

/**
 * 包装路由的初始化，并注册一个路由实例供其他模块使用
 */
@singleton()
@registry([{
  token: CORE_INITIALIZER,
  useFactory: instanceCachingFactory<Initializer>(c => c.resolve(RouterInitializer))
}])
@injectable()
export class RouterInitializer extends RouteExtend implements Initializer {
  options: RouteExtendOptions
  router: Router | undefined
  constructor(
    @injectOptional(RouteExtendInjectKey)
    options: RouteExtendOptions
  ) {
    super()
    this.options = options
  }

  start(): void {
    this.router = createRouter({
      history: createWebHistory(import.meta.env.BASE_URL),
      routes: [] // @add by other
    })
    container.register<Router>(CORE_ROUTER, { useValue: this.router })
    container.register<RouteExtend>(CORE_ROUTER_EXTEND, { useClass: RouteExtend })
  }

  started(app: App<Element>): void {
    if (this.router) {
      app.use(this.router)
    }
    if (this.options?.enable) {
      this.moduleStart()
    }
  }

  /**
   * @description 服务启动前置工作，必须得在启动项中作处理
   */
  moduleStart(): void {
    // 执行服务方法
    this.setOptions(this.options)
  }
}
